import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AvailabilityService } from './availability.service';
import { SettingsService } from './settings.service';
import { PIMService } from './pim.service';
import { IAlternativeAvailabilityResult, IAlternativeHotelResult, IEndpoint, ILaterStayOption, ISearchParams } from '../interfaces';

/** Values of the availability.alternativeAvailabilityLaterStay setting (anything else, e.g. NONE, means off). */
const LATER_STAY_OPTIONS: Record<string, ILaterStayOption> = {
  ONE_WEEK: { amount: 1, unit: 'week', maxSteps: 8, titleKey: 'av.alternativeAvailability.laterStayOneWeek' },
  TWO_WEEKS: { amount: 2, unit: 'week', maxSteps: 4, titleKey: 'av.alternativeAvailability.laterStayTwoWeeks' },
  ONE_MONTH: { amount: 1, unit: 'month', maxSteps: 3, titleKey: 'av.alternativeAvailability.laterStayOneMonth' },
};

/**
 * Settings gates and date-shift math for the alternative availability feature
 * (suggesting other hotels, shifted dates, or next-week dates when the
 * originally requested search has no availability).
 */
@Injectable({
  providedIn: 'root'
})
export class AlternativeAvailabilityService {

  constructor(
    private settings: SettingsService,
    private availabilityService: AvailabilityService,
    private pimService: PIMService,
  ) { }

  isDayShiftEnabled(): boolean {
    return !!this.settings.get('availability.alternativeAvailabilityShowDayShift');
  }

  /** The configured offset of the "same stay later" box, or null when the box is off. */
  getLaterStayOption(): ILaterStayOption | null {
    return LATER_STAY_OPTIONS[this.settings.get('availability.alternativeAvailabilityLaterStay')] ?? null;
  }

  shiftDateRange(arrival: string, departure: string, days: number): { arrival: string; departure: string } {
    return {
      arrival: dayjs(arrival).add(days, 'day').format('YYYY-MM-DD'),
      departure: dayjs(departure).add(days, 'day').format('YYYY-MM-DD'),
    };
  }

  /**
   * Other hotels to suggest: this tenant's own other hotels (searched from here, one request each)
   * plus the cross-tenant hotels configured backend-only, which the backend looks up for us. The
   * frontend cannot see that configuration, so the backend is always asked; a tenant without it
   * answers with an empty list. Same-tenant hotels come first.
   */
  getOtherHotels(searchParams: ISearchParams): Observable<IAlternativeHotelResult[]> {
    const arrival = this.availabilityService.toApiDate(searchParams.arrival as string);
    const departure = this.availabilityService.toApiDate(searchParams.departure as string);

    const endpoints: IEndpoint[] = this.settings.get('app.endpoints') ?? [];
    const sameTenantSearches = endpoints
      .filter(endpoint => endpoint.hotelCode !== searchParams.hotelCode)
      .map(endpoint => this.availabilityService.searchAvailabilityFor({
        hotelCode: endpoint.hotelCode,
        arrival,
        departure,
        roomData: searchParams.roomData,
      }).pipe(
        map(rooms => this.availabilityService.summarizeAvailability(rooms[0])),
        catchError(() => of({ minPrice: null, roomsAvailable: 0 })),
        map((summary): IAlternativeHotelResult => ({
          hotelCode: endpoint.hotelCode,
          displayName: this.getHotelDisplayName(endpoint.hotelCode),
          hasAvailability: summary.roomsAvailable > 0,
          ...summary,
        })),
      ));

    const crossTenantSearch = this.availabilityService.searchAlternativeOtherHotels({
      arrival,
      departure,
      roomData: searchParams.roomData,
    }).pipe(
      map(hotels => hotels.map((hotel): IAlternativeHotelResult => ({ ...hotel }))),
      catchError(() => of([] as IAlternativeHotelResult[])),
    );

    return forkJoin({
      sameTenant: sameTenantSearches.length ? forkJoin(sameTenantSearches) : of([] as IAlternativeHotelResult[]),
      crossTenant: crossTenantSearch,
    }).pipe(
      map(({ sameTenant, crossTenant }) => [...sameTenant, ...crossTenant]),
    );
  }

  getDayShift(searchParams: ISearchParams): Observable<{ previous: IAlternativeAvailabilityResult; next: IAlternativeAvailabilityResult }> {
    return forkJoin({
      previous: this.getShifted(searchParams, -1),
      next: this.getShifted(searchParams, 1),
    });
  }

  /** Availability for the original stay shifted by the given number of option units (weeks or months). */
  getLaterStay(searchParams: ISearchParams, option: ILaterStayOption, steps = 1): Observable<IAlternativeAvailabilityResult> {
    const arrival = this.availabilityService.toApiDate(searchParams.arrival as string);
    const days = dayjs(arrival).add(option.amount * steps, option.unit).diff(dayjs(arrival), 'day');
    return this.getShifted(searchParams, days);
  }

  /** Availability for the original stay shifted by the given number of days (negative = earlier). */
  getShifted(searchParams: ISearchParams, days: number): Observable<IAlternativeAvailabilityResult> {
    const arrival = this.availabilityService.toApiDate(searchParams.arrival as string);
    const departure = this.availabilityService.toApiDate(searchParams.departure as string);

    return this.searchShiftedRange(searchParams, this.shiftDateRange(arrival, departure, days));
  }

  /**
   * Resolves a display name for a same-tenant hotel code via PIMService,
   * falling back to the raw hotel code if PIM data is missing/null OR if
   * PIMService.getHotelInfo throws (e.g. unconfigured/failed-to-load PIM data).
   */
  private getHotelDisplayName(hotelCode: string): string {
    try {
      return this.pimService.getHotelInfo(hotelCode)?.name ?? hotelCode;
    } catch {
      return hotelCode;
    }
  }

  private searchShiftedRange(
    searchParams: ISearchParams,
    range: { arrival: string; departure: string },
  ): Observable<IAlternativeAvailabilityResult> {
    return this.availabilityService.searchAvailabilityFor({
      hotelCode: searchParams.hotelCode,
      arrival: range.arrival,
      departure: range.departure,
      roomData: searchParams.roomData,
      bookingCode: searchParams.bookingCode,
    }).pipe(
      map(rooms => this.availabilityService.summarizeAvailability(rooms[0])),
      map(summary => ({
        ...range,
        hasAvailability: summary.roomsAvailable > 0,
        ...summary,
      })),
    );
  }
}
